import { debounce, chain, assign, map } from 'lodash';
import { DateTime } from 'luxon';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'querystring';
import * as XLSX from 'xlsx';
import toast from '../Toast/Toast';

import Button from '../ReusableComponents/Button/Button';
import HeaderCell from '../ReusableComponents/Table/HeaderCell';
import HeaderRow from '../ReusableComponents/Table/HeaderRow';
import LinearProgress from '../ReusableComponents/LinearProgress';
import TableBody from '../ReusableComponents/Table/TableBody';
import Table from '../ReusableComponents/Table/Table';
import TableHead from '../ReusableComponents/Table/TableHead';
import TablePaper from '../ReusableComponents/Table/TablePaper';
import GradeUpload from './GradeUpload';
import vendorGradeHeaders from '../../Lib/Utils/Table/Headers/vendor-grade-headers';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';
import historyProps from '../../Lib/Utils/PropTypes/history-props';
import locationProps from '../../Lib/Utils/PropTypes/location-props';
import VendorGradeRow from './VendorGradeRow';
import Search from '../ReusableComponents/Search';
import Pagination from '../ReusableComponents/Pagination';
import styles from './DeliveryManagement.module.css';
import { ReactComponent as Download } from '../../Assets/icons/download.svg';

import { vendorGradeExport } from '../../Lib/Utils/Table/table-export';
import VendorAPI from '../../Lib/Api/vendor.ts';

/**
 * Creates the table headers from objects imported here.
 *
 * @param {arrayOf} headerArr array of header value objects
 *  @property {object} header individual objects with header values
 *    @property {string} label the label value
 *    @property {number} key unique key for react components
 *
 * @returns {object} header cell components to be used by the table.
 */
const createHeaders = (headerArr) =>
  headerArr.map(({ key, value }) => (
    <HeaderCell
      key={key}
      padding="normal"
      align="center"
      className={styles.header}
    >
      {value}
    </HeaderCell>
  ));

class VendorGrades extends Component {
  constructor(props) {
    super(props);

    const {
      fetchVendors,
      location: { search },
    } = this.props;

    this.state = {
      exporting: false,
      uploading: false,
      failedUploads: [],
    };

    this.exportVendors = this.exportVendors.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.updateUrl = this.updateUrl.bind(this);
    this.onFileUpload = this.onFileUpload.bind(this);

    fetchVendors(search.slice(1));
  }

  /**
   * CSV/Excel file must be formatted 2 columns:
   * col 1 - company_name
   * col 2 - grade
   * @param file
   */
  onFileUpload(file) {
    const {
      fetchVendors,
      location: { search },
    } = this.props;

    const reader = new FileReader();
    const rABS = !!reader.readAsBinaryString;

    this.setState({ uploading: true });

    reader.onload = (e) => {
      const workbook = XLSX.read(e.target.result, {
        type: rABS ? 'binary' : 'array',
        cellDates: 'd',
      });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      const { validGrades, validationErrors } = rows.slice(1).reduce(
        (acc, row, index) => {
          try {
            if (!row[0] || !row[1]) {
              throw new Error('Missing company name or grade');
            }

            acc.validGrades.push({
              company_name: String(row[0]).trim(),
              grade: String(row[1]).trim(),
              grade_updated_at: DateTime.utc().toUTC().toJSDate(),
            });
          } catch (error) {
            acc.validationErrors.push({
              row: index + 2,
              company_name: row[0]?.trim() || 'Missing Vendor',
              error: error.message,
            });
          }
          return acc;
        },
        { validGrades: [], validationErrors: [] },
      );

      this.setState({ failedUploads: validationErrors });

      if (validGrades.length > 0) {
        VendorAPI.updateVendorGrades(validGrades)
          .then((response) => {
            // Ensure response is an array and aggregate all results
            const responses = Array.isArray(response) ? response : [response];
            const aggregatedResults = responses.reduce(
              (acc, chunk) => ({
                successful: [
                  ...(acc.successful || []),
                  ...(chunk.successful || []),
                ],
                errors: [...(acc.errors || []), ...(chunk.errors || [])],
              }),
              { successful: [], errors: [] },
            );

            if (aggregatedResults.successful.length > 0) {
              toast(
                {
                  title: `Successfully updated ${aggregatedResults.successful.length} grades${
                    validationErrors.length
                      ? ` (${validationErrors.length} rows had validation errors)`
                      : ''
                  }`,
                },
                { type: 'success' },
              );
            }

            // Collect all API errors
            if (aggregatedResults.errors.length > 0) {
              const apiErrors = aggregatedResults.errors.map((error) => ({
                row: 'N/A',
                company_name: error.company_name,
                error: error.message,
              }));
              this.setState((prevState) => ({
                failedUploads: [...prevState.failedUploads, ...apiErrors],
              }));
            }

            fetchVendors(search.slice(1));
          })
          .catch((err) => {
            const errorData = err.response?.data;
            if (errorData?.errors?.length > 0) {
              const apiErrors = errorData.errors.map((error) => ({
                row: '-',
                company_name: error.company_name,
                error: error.message,
              }));
              this.setState((prevState) => ({
                failedUploads: [...prevState.failedUploads, ...apiErrors],
              }));
            } else {
              toast(
                { title: errorData?.message || 'Failed to update grades' },
                { type: 'error' },
              );
            }
          })
          .finally(() => {
            this.setState({ uploading: false });
          });
      } else {
        this.setState({ uploading: false });
      }
    };

    if (rABS) {
      reader.readAsBinaryString(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  }

  /**
   * Adds query parameters to the url to update the query to our api and calls
   * fetchVendors
   *
   * @param {string} name the name of the param to be added to the url
   * @param {string} value the value being added to the url
   *
   * @returns undefined
   */
  updateUrl(name, value) {
    const {
      history,
      location: { search: params, pathname },
    } = this.props;
    const { fetchVendors } = this.props;
    const search = chain(params.slice(1))
      .thru(queryString.parse)
      .assign({ [name]: value })
      .thru((p) => (name === 'search' ? assign(p, { page: 1 }) : p))
      .pickBy()
      .thru(queryString.encode)
      .value();

    history.push({ pathname, search });

    if (name === 'search') this.handleChangePage(1);

    fetchVendors(search);
  }

  /**
   * Updates the url to increment, decrement, or navigate directly to a
   * specific results page.
   *
   * @param {number} page
   */
  handleChangePage(page) {
    this.updateUrl('page', page);
  }

  exportVendors() {
    this.setState({ exporting: true });
    vendorGradeExport().then(() => {
      this.setState({ exporting: false });
    });
  }

  render() {
    const { exporting, failedUploads } = this.state;
    const { location, vendors, vendorCount, loading } = this.props;
    const params = decodeLocation(location.search);

    return (
      <div className={styles.gradesContainer}>
        <div className={styles.headerContainer}>
          <h3 className={styles.header}>Vendor Grades</h3>
          <div className={styles.downloadAndSearchContainer}>
            <Button
              variant="outlined"
              id="download-button"
              color="primary"
              onClick={this.exportVendors}
              small
              icon
              flex
              className={styles.downloadButton}
            >
              <Download className={styles.iconPadLeft} />
              {exporting ? 'Loading...' : 'Download'}
            </Button>
            <Search
              updateUrl={debounce(this.updateUrl, 500)}
              location={location}
            />
          </div>
        </div>
        <div className={styles.tableContainer}>
          <TablePaper>
            <div className={styles.scrollableContainer}>
              <LinearProgress loading={loading} />
              <Table>
                <TableHead>
                  <HeaderRow>{createHeaders(vendorGradeHeaders)}</HeaderRow>
                </TableHead>
                <TableBody>
                  {map(vendors, (vendor) => (
                    <VendorGradeRow key={vendor.id} vendor={vendor} />
                  ))}
                </TableBody>
              </Table>
            </div>
            <Pagination
              count={vendorCount}
              page={+params.page || 1}
              onChangePage={this.handleChangePage}
              ActionsComponent={Pagination}
            />
          </TablePaper>
        </div>
        <div className={styles.uploadContainer}>
          <GradeUpload
            header="Upload Grades"
            name="grade-upload"
            fullWidth
            onChange={this.onFileUpload}
            loading={this.state.uploading}
          />
          {!this.state.uploading && failedUploads.length > 0 && (
            <div className={styles.errorContainer}>
              <h4 className={styles.errorHeader}>Failed Uploads</h4>
              <div className={styles.errorList}>
                {failedUploads.map((failure) => (
                  <div
                    key={`${failure.company_name}-${failure.row}-${failure.error}`}
                    className={styles.errorItem}
                  >
                    {failure.company_name} - {failure.error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

VendorGrades.propTypes = {
  loading: PropTypes.bool.isRequired,
  location: locationProps.isRequired,
  history: historyProps.isRequired,
  vendors: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  vendorCount: PropTypes.number.isRequired,
  fetchVendors: PropTypes.func.isRequired,
};

export default VendorGrades;
